import React, { useRef, useEffect, useState } from "react";
import {
	Box,
	Typography,
	Avatar,
	Tooltip,
	CircularProgress,
	IconButton,
	Popover,
	Dialog,
	Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddReactionIcon from "@mui/icons-material/AddReaction";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { Conversation, Message } from "@/types/chat";
import { SharedRecordCard } from "./SharedRecordCard";

interface ChatMessageListProps {
	conversation: Conversation;
	messages: Message[];
	user: any;
	typingUsers: string[];
	isLoadingMore?: boolean;
	hasMore?: boolean;
	onLoadMore?: (conversationId: string) => void;
	onReact?: (
		conversationId: string,
		messageId: string,
		emoji: string,
		action: "add" | "remove",
	) => void;
	reactionAnchorEl: null | HTMLElement;
	setReactionAnchorEl: (el: null | HTMLElement) => void;
	activeMessageId: string | null;
	setActiveMessageId: (id: string | null) => void;
}

const REACTION_EMOJIS: Record<string, string> = {
	LIKE: "👍",
	HEART: "❤️",
	LAUGH: "😂",
	SURPRISE: "😮",
	SAD: "😢",
	ANGRY: "😡",
};

const getFullUrl = (url: string) => {
	if (!url || url.startsWith("http")) return url;
	const baseUrl = process.env.REACT_APP_CDN_URL
		? process.env.REACT_APP_CDN_URL.replace("/api", "")
		: "";
	return `${baseUrl}${url.startsWith("/") ? "" : "/"}${url}`;
};

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
	conversation,
	messages,
	user,
	typingUsers,
	isLoadingMore,
	hasMore,
	onLoadMore,
	onReact,
	reactionAnchorEl,
	setReactionAnchorEl,
	activeMessageId,
	setActiveMessageId,
}) => {
	const theme = useTheme();
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const messagesContainerRef = useRef<HTMLDivElement>(null);
	const prevScrollHeightRef = useRef<number | null>(null);
	const [selectedImage, setSelectedImage] = useState<string | null>(null);

	// Auto scroll to bottom initially or when sending/receiving new message
	useEffect(() => {
		if (prevScrollHeightRef.current !== null && messagesContainerRef.current) {
			// We just loaded older messages, adjust scroll to maintain position
			messagesContainerRef.current.scrollTop =
				messagesContainerRef.current.scrollHeight - prevScrollHeightRef.current;
			prevScrollHeightRef.current = null;
		} else {
			// Normal new message, scroll to bottom
			setTimeout(() => {
				messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
			}, 100);
		}
	}, [messages, typingUsers]);

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		if (
			e.currentTarget.scrollTop === 0 &&
			hasMore &&
			!isLoadingMore &&
			conversation &&
			onLoadMore
		) {
			prevScrollHeightRef.current = e.currentTarget.scrollHeight;
			onLoadMore(conversation.id);
		}
	};

	const handleReactionClick = (
		event: React.MouseEvent<HTMLElement>,
		messageId: string,
	) => {
		setReactionAnchorEl(event.currentTarget);
		setActiveMessageId(messageId);
	};

	const handleEmojiSelect = (emojiKey: string) => {
		if (conversation && activeMessageId && onReact) {
			onReact(conversation.id, activeMessageId, emojiKey, "add");
		}
		setReactionAnchorEl(null);
		setActiveMessageId(null);
	};

	const handleReactionToggle = (
		messageId: string,
		emojiKey: string,
		hasReacted: boolean,
	) => {
		if (conversation && onReact) {
			onReact(
				conversation.id,
				messageId,
				emojiKey,
				hasReacted ? "remove" : "add",
			);
		}
	};

	const handleRecordLoad = () => {
		if (messagesContainerRef.current) {
			setTimeout(() => {
				messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
			}, 150);
		}
	};

	const isOthersTyping = typingUsers.some((id) => id !== user?.id);

	return (
		<>
			<Box
				ref={messagesContainerRef}
				onScroll={handleScroll}
				sx={{
					flexGrow: 1,
					p: 2,
					overflowY: "auto",
					display: "flex",
					flexDirection: "column",
					backgroundColor:
						theme.palette.mode === "dark"
							? "transparent" // let the glassmorphism show through
							: "rgba(240,244,248,0.5)",
					"&::-webkit-scrollbar": { width: 6 },
					"&::-webkit-scrollbar-thumb": {
						backgroundColor: "rgba(150,150,150,0.3)",
						borderRadius: 3,
					},
				}}
			>
				{isLoadingMore && (
					<Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
						<CircularProgress size={20} />
					</Box>
				)}

				{messages.map((msg, idx) => {
					const isMe = msg.senderId === user?.id;
					const showAvatar =
						!isMe &&
						(idx === 0 || messages[idx - 1].senderId !== msg.senderId);

					const isGroup = conversation?.type === "GROUP";
					const sender = conversation?.participants?.find((p) => p.id === msg.senderId);
					const senderName = sender
						? `${sender.firstName || ""} ${sender.lastName || ""}`.trim() || sender.username
						: "Unknown User";

					const isNextSameSender =
						idx < messages.length - 1 &&
						messages[idx + 1].senderId === msg.senderId && messages[idx + 1].type !== "SYSTEM";
					const defaultMb = isNextSameSender ? 1 : 2.5;

					const renderContentWithLinks = (content: string) => {
						const urlRegex = /(https?:\/\/[^\s]+)/g;
						const parts = content.split(urlRegex);
						return parts.map((part, i) => {
							if (part.match(urlRegex)) {
								const isInternal = part.startsWith(window.location.origin);
								if (isInternal) {
									const path = part.replace(window.location.origin, "");
									return (
										<MuiLink component={RouterLink} to={path} key={i} sx={{ color: "inherit", textDecoration: "underline", wordBreak: "break-all" }}>
											{part}
										</MuiLink>
									);
								} else {
									return (
										<MuiLink href={part} target="_blank" rel="noopener noreferrer" key={i} sx={{ color: "inherit", textDecoration: "underline", wordBreak: "break-all" }}>
											{part}
										</MuiLink>
									);
								}
							}
							return part;
						});
					};

					const sharedRecordMatch = msg.content?.match(
						/^\$\$SHARE:(CUSTOMER|CONTRACT|INVOICE):(.+)\$\$/,
					);

					return (
						<Box
							key={msg.id || idx}
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: isMe ? "flex-end" : "flex-start",
								mb:
									msg.reactions && msg.reactions.length > 0 ? 2.5 : defaultMb,
								position: "relative",
								width: "100%",
							}}
						>
							{msg.type === "SYSTEM" ? (
								<Box sx={{ width: "100%", display: "flex", justifyContent: "center", my: 1 }}>
									<Box sx={{ 
										px: 2, 
										py: 0.5, 
										borderRadius: 4, 
										backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
										color: 'text.secondary',
										fontSize: '0.75rem',
										textAlign: 'center'
									}}>
										{msg.content}
									</Box>
								</Box>
							) : sharedRecordMatch ? (
								<Box sx={{ width: "100%" }}>
									<SharedRecordCard
										type={sharedRecordMatch[1] as any}
										id={sharedRecordMatch[2]}
										onLoad={handleRecordLoad}
									/>
								</Box>
							) : (
								<Box sx={{ display: "flex", width: "100%", justifyContent: isMe ? "flex-end" : "flex-start", gap: 1 }}>
									{isGroup && !isMe && (
										<Box sx={{ width: 32, flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
											{showAvatar && (
												<Tooltip title={senderName} placement="left">
													<Avatar src={sender?.avatar} sx={{ width: 32, height: 32, mb: 1 }}>
														{sender?.firstName?.[0] || sender?.username?.[0] || "?"}
													</Avatar>
												</Tooltip>
											)}
										</Box>
									)}
									<Box sx={{ display: "flex", flexDirection: "column", maxWidth: "75%", position: "relative" }}>
										{isGroup && !isMe && showAvatar && (
											<Typography variant="caption" sx={{ ml: 1, mb: 0.5, color: "text.secondary", fontWeight: 600 }}>
												{senderName}
											</Typography>
										)}
										<Tooltip
											title={moment(msg.createdAt).format("LLL")}
											placement={isMe ? "left" : "right"}
											enterDelay={500}
										>
										<Box
											sx={{
												p: 1.5,
												borderRadius: 2.5,
												borderBottomRightRadius: isMe ? 4 : 20,
												borderBottomLeftRadius: !isMe ? 4 : 20,
												background: isMe
											? theme.palette.mode === "dark"
												? "linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)"
												: theme.palette.primary.main
											: theme.palette.mode === "dark"
												? "rgba(255, 255, 255, 0.08)"
												: theme.palette.background.paper,
										color: isMe ? "#ffffff" : theme.palette.text.primary,
										boxShadow:
											theme.palette.mode === "dark"
												? "0 4px 12px rgba(0,0,0,0.2)"
												: theme.shadows[1],
										border:
											!isMe && theme.palette.mode === "dark"
												? "1px solid rgba(255,255,255,0.05)"
												: "none",
										position: "relative",
										"&:hover .reaction-btn": {
											opacity: 1,
										},
									}}
								>
									{/* Attachments */}
									{msg.attachments && msg.attachments.length > 0 && (
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												gap: 1,
												mb: msg.content ? 1 : 0,
											}}
										>
											{msg.attachments.map((att) =>
												att.fileType.startsWith("image/") ? (
													<Box
														key={att.id}
														onClick={() => setSelectedImage(getFullUrl(att.fileUrl))}
														sx={{
															cursor: "pointer",
															borderRadius: 2,
															overflow: "hidden",
															display: "flex",
															"&:hover img": {
																transform: "scale(1.02)",
															}
														}}
													>
														<img
															src={getFullUrl(att.fileUrl)}
															alt={att.fileName}
															style={{
																maxWidth: "100%",
																borderRadius: 8,
																transition: "transform 0.2s ease",
															}}
														/>
													</Box>
												) : (
													<Box
														key={att.id}
														sx={{
															p: 1,
															borderRadius: 1,
															backgroundColor:
																theme.palette.mode === "dark"
																	? "rgba(0,0,0,0.25)"
																	: "rgba(0,0,0,0.1)",
															display: "flex",
															alignItems: "center",
															gap: 1,
															maxWidth: "100%",
															overflow: "hidden",
														}}
													>
														<AttachFileIcon
															fontSize="small"
															sx={{ flexShrink: 0 }}
														/>
														<a
															href={getFullUrl(att.fileUrl)}
															target="_blank"
															rel="noopener noreferrer"
															style={{
																color: "inherit",
																textDecoration: "none",
																overflow: "hidden",
																textOverflow: "ellipsis",
															}}
														>
															<Typography
																variant="caption"
																noWrap
																sx={{
																	display: "block",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																	cursor: "pointer",
																	"&:hover": {
																		textDecoration: "underline",
																	},
																}}
															>
																{att.fileName}
															</Typography>
														</a>
													</Box>
												),
											)}
										</Box>
									)}

									{msg.content && (
										<Typography
											variant="body2"
											sx={{ wordBreak: "break-word" }}
										>
											{renderContentWithLinks(msg.content)}
										</Typography>
									)}

									{/* Reaction Button (Hover) */}
									{msg.id !== msg.tempId && (
										<IconButton
											className="reaction-btn"
											size="small"
											onClick={(e) => handleReactionClick(e, msg.id)}
											sx={{
												position: "absolute",
												bottom: -12,
												[isMe ? "left" : "right"]: -12,
												backgroundColor:
													theme.palette.mode === "dark"
														? "#333"
														: theme.palette.background.paper,
												boxShadow:
													theme.palette.mode === "dark"
														? "0 2px 8px rgba(0,0,0,0.5)"
														: 1,
												opacity: 0,
												transition: "opacity 0.2s",
												"&:hover": {
													backgroundColor:
														theme.palette.mode === "dark"
															? "#444"
															: theme.palette.background.default,
												},
											}}
										>
											<AddReactionIcon
												sx={{ fontSize: 14, color: "text.secondary" }}
											/>
										</IconButton>
									)}

									{/* Reactions Display */}
									{msg.reactions && msg.reactions.length > 0 && (
										<Box
											sx={{
												position: "absolute",
												bottom: -18,
												[isMe ? "right" : "left"]: 0,
												display: "flex",
												flexWrap: "wrap",
												gap: 0.5,
												zIndex: 2,
											}}
										>
											{Object.entries(
												msg.reactions.reduce(
													(acc, r) => {
														acc[r.emoji] = (acc[r.emoji] || 0) + 1;
														return acc;
													},
													{} as Record<string, number>,
												),
											).map(([emojiKey, count]) => {
												const hasReacted = msg.reactions?.some(
													(r) =>
														r.userId === user?.id && r.emoji === emojiKey,
												);
												return (
													<Box
														key={emojiKey}
														onClick={() =>
															handleReactionToggle(
																msg.id,
																emojiKey,
																!!hasReacted,
															)
														}
														sx={{
															backgroundColor: hasReacted
																? theme.palette.mode === "dark"
																	? "rgba(60, 120, 200, 0.4)"
																	: "primary.light"
																: theme.palette.mode === "dark"
																	? "rgba(255, 255, 255, 0.1)"
																	: "background.paper",
															color: hasReacted
																? theme.palette.mode === "dark"
																	? "#90caf9"
																	: "primary.contrastText"
																: "text.primary",
															borderRadius: 4,
															px: 0.75,
															py: 0.25,
															fontSize: "0.75rem",
															display: "flex",
															alignItems: "center",
															gap: 0.5,
															cursor: "pointer",
															border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : theme.palette.divider}`,
															boxShadow:
																theme.palette.mode === "dark"
																	? "none"
																	: theme.shadows[1],
														}}
													>
														<span>
															{REACTION_EMOJIS[emojiKey] || emojiKey}
														</span>
														<span style={{ fontWeight: "bold" }}>
															{count}
														</span>
													</Box>
												);
											})}
										</Box>
									)}
								</Box>
							</Tooltip>
							</Box>
							</Box>
							)}
						</Box>
					);
				})}

				{isOthersTyping && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Avatar sx={{ width: 28, height: 28 }} />
						<Box
							sx={{
								p: 1.5,
								borderRadius: 2.5,
								backgroundColor:
									theme.palette.mode === "dark"
										? "rgba(255, 255, 255, 0.08)"
										: theme.palette.background.paper,
								border:
									theme.palette.mode === "dark"
										? "1px solid rgba(255,255,255,0.05)"
										: "none",
								display: "flex",
								gap: 0.5,
							}}
						>
							<Box
								sx={{
									width: 6,
									height: 6,
									borderRadius: "50%",
									backgroundColor: "text.disabled",
									animation: "bounce 1.4s infinite ease-in-out both",
								}}
							/>
							<Box
								sx={{
									width: 6,
									height: 6,
									borderRadius: "50%",
									backgroundColor: "text.disabled",
									animation: "bounce 1.4s infinite ease-in-out both",
									animationDelay: "0.2s",
								}}
							/>
							<Box
								sx={{
									width: 6,
									height: 6,
									borderRadius: "50%",
									backgroundColor: "text.disabled",
									animation: "bounce 1.4s infinite ease-in-out both",
									animationDelay: "0.4s",
								}}
							/>
						</Box>
					</Box>
				)}

				<style>
					{`
          @keyframes bounce {
            0%, 80%, 100% { transform: scale(0); }
            40% { transform: scale(1); }
          }
        `}
				</style>

				<div ref={messagesEndRef} />
			</Box>

			{/* Reaction Popover */}
			<Popover
				open={Boolean(reactionAnchorEl)}
				anchorEl={reactionAnchorEl}
				onClose={() => setReactionAnchorEl(null)}
				disableScrollLock={true}
				anchorOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				PaperProps={{
					sx: { p: 0.5, borderRadius: 8, display: "flex", gap: 0.5 },
				}}
			>
				{Object.entries(REACTION_EMOJIS).map(([key, emoji]) => (
					<IconButton
						key={key}
						size="small"
						onClick={() => handleEmojiSelect(key)}
					>
						{emoji}
					</IconButton>
				))}
			</Popover>

			{/* Image Viewer Dialog */}
			<Dialog
				open={Boolean(selectedImage)}
				onClose={() => setSelectedImage(null)}
				maxWidth="md"
				PaperProps={{
					sx: {
						backgroundColor: "transparent",
						boxShadow: "none",
					},
				}}
			>
				{selectedImage && (
					<Box sx={{ position: "relative" }}>
						<IconButton
							onClick={() => setSelectedImage(null)}
							sx={{
								position: "absolute",
								top: 8,
								right: 8,
								color: "white",
								backgroundColor: "rgba(0,0,0,0.5)",
								"&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
							}}
						>
							<CloseIcon />
						</IconButton>
						<img
							src={selectedImage}
							alt="Attachment"
							style={{
								maxWidth: "100%",
								maxHeight: "90vh",
								objectFit: "contain",
								borderRadius: 8,
							}}
						/>
					</Box>
				)}
			</Dialog>
		</>
	);
};
